import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { Box, Tooltip, Typography } from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import {
  ISkus,
  ListRequestOfBaseFilter,
  ListRequestOfSkuRelationshipFilter,
  ListResponseOfParentSku,
  ListResponseOfSkuRelationship,
  ParentSku,
  SkuRelationshipFilter,
} from "services/KnowfullyAdminClient";
import { SkuRelationshipTypeIds } from "types/common-types";
import { SkuRelatedSkuWithId } from "types/sku-related-types";
import { SkuContents } from "../SkuContents";

const styles = {
  link: {
    textDecoration: "none",
    color: "inherit",
  },
};

const SkuRelationships = ({
  skuId,
  sku,
}: {
  skuId: string;
  sku?: ISkus | null;
}) => {
  const { formatMessage: t } = useIntl();
  const { skuService, skuDetailsUrl } = useGlobalContext();

  const [autoRegisterPage, setAutoRegisterPage] = useState(0);
  const [autoRegisterRowsPerPage, setAutoRegisterRowsPerPage] = useState(
    DEFAULT_ROWS_PER_PAGE,
  );
  const [suggestedCoursesPage, setSuggestedCoursesPage] = useState(0);
  const [
    suggestedCoursesRowsPerPage,
    setSuggestedCoursesRowsPerPage,
  ] = useState(DEFAULT_ROWS_PER_PAGE);
  const [includedInPage, setIncludedInPage] = useState(0);
  const [includedInRowsPerPage, setIncludedInRowsPerPage] = useState(
    DEFAULT_ROWS_PER_PAGE,
  );
  const [includedInOrder, setIncludedInOrder] = useState<TableSortType>("asc");
  const [includedInOrderBy, setIncludedInOrderBy] = useState("parentSku.Sku");

  const {
    data: autoRegisterData,
    isFetching: isFetchingAutoRegister,
  } = useStrictQuery<ListResponseOfSkuRelationship>(
    [
      "skuService.getChildSkusRelated",
      skuId,
      SkuRelationshipTypeIds.AutoRegister,
      autoRegisterPage,
      autoRegisterRowsPerPage,
    ],
    () =>
      skuService.getChildSkusRelated(
        new ListRequestOfSkuRelationshipFilter({
          pageNumber: autoRegisterPage,
          pageSize: autoRegisterRowsPerPage,
          parameters: new SkuRelationshipFilter({
            skuId,
            skuRelationshipTypeId: SkuRelationshipTypeIds.AutoRegister,
          }),
        }),
      ),
    { enabled: !!skuId, refetchOnMount: "always" },
  );

  const {
    data: suggestedCoursesData,
    isFetching: isFetchingSuggestedCourses,
  } = useStrictQuery<ListResponseOfSkuRelationship>(
    [
      "skuService.getChildSkusRelated",
      skuId,
      SkuRelationshipTypeIds.SuggestedCourses,
      suggestedCoursesPage,
      suggestedCoursesRowsPerPage,
    ],
    () =>
      skuService.getChildSkusRelated(
        new ListRequestOfSkuRelationshipFilter({
          pageNumber: suggestedCoursesPage,
          pageSize: suggestedCoursesRowsPerPage,
          parameters: new SkuRelationshipFilter({
            skuId,
            skuRelationshipTypeId: SkuRelationshipTypeIds.SuggestedCourses,
          }),
        }),
      ),
    { enabled: !!skuId, refetchOnMount: "always" },
  );

  const {
    data: includedInData,
    isFetching: isFetchingIncludedIn,
  } = useQuery<ListResponseOfParentSku>(
    [
      "skuService.getParentSkusOfChildSku",
      skuId,
      includedInPage,
      includedInRowsPerPage,
      includedInOrder,
      includedInOrderBy,
    ],
    () =>
      skuService.getParentSkusOfChildSku(
        skuId,
        ListRequestOfBaseFilter.fromJS({
          pageNumber: includedInPage,
          pageSize: includedInRowsPerPage,
          sortField: includedInOrderBy,
          sortDescending: includedInOrder === "desc",
        }),
      ),
    { enabled: !!skuId, refetchOnMount: "always" },
  );

  const autoRegisterSkus = useMemo(
    () => (autoRegisterData?.data ?? []) as SkuRelatedSkuWithId[],
    [autoRegisterData?.data],
  );
  const suggestedCoursesSkus = useMemo(
    () => (suggestedCoursesData?.data ?? []) as SkuRelatedSkuWithId[],
    [suggestedCoursesData?.data],
  );
  const includedInSkus = useMemo(() => includedInData?.data ?? [], [
    includedInData?.data,
  ]);

  const SkuTitle = ({ value }: { value: SkuRelatedSkuWithId }) => (
    <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
      {value.isBidirectional && (
        <Tooltip title={t({ id: "bidirectional" })}>
          <SyncAltIcon fontSize="small" color="primary" />
        </Tooltip>
      )}
      <Link to={skuDetailsUrl(value.childSkuId)} style={styles.link}>
        {value.title}
      </Link>
    </Box>
  );

  const SkuName = ({ value }: { value: SkuRelatedSkuWithId }) => (
    <Link to={skuDetailsUrl(value.childSkuId)} style={styles.link}>
      {value.name}
    </Link>
  );

  const ParentSkuName = ({ value }: { value: ParentSku }) => (
    <Link to={skuDetailsUrl(value.skuId)} style={styles.link}>
      {value.sku}
    </Link>
  );

  const ParentSkuTitle = ({ value }: { value: ParentSku }) => (
    <Link to={skuDetailsUrl(value.skuId)} style={styles.link}>
      {value.title}
    </Link>
  );

  const skuRelationshipCells: Array<TableCellType> = [
    {
      accessor: "value",
      align: "left",
      width: "83%",
      type: "node",
      label: t({ id: "skuRelationShipSkuTitleLabel" }),
      View: SkuTitle,
      noSort: true,
    },
    {
      accessor: "value",
      align: "left",
      width: "17%",
      type: "node",
      label: t({ id: "skuRelationShipSkuNameLabel" }),
      View: SkuName,
      noSort: true,
    },
  ];

  const includedInCells: Array<TableCellType> = [
    {
      accessor: "sku",
      customSortAccessor: "parentSku.Sku",
      align: "left",
      width: 0,
      type: "node",
      label: t({ id: "skuRelationShipSkuNameLabel" }),
      View: ParentSkuName,
    },
    {
      accessor: "title",
      customSortAccessor: "parentSku.Title",
      align: "left",
      width: 0,
      type: "node",
      label: t({ id: "skuRelationShipSkuTitleLabel" }),
      View: ParentSkuTitle,
    },
  ];

  return (
    <Box>
      {autoRegisterSkus.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <T id="autoRegisterTitle" />
          </Typography>
          <LazyTable
            {...{
              rowCount: autoRegisterData?.totalRecords ?? 0,
              rows: autoRegisterSkus,
              loading: isFetchingAutoRegister,
              dense: true,
              cells: skuRelationshipCells,
              page: autoRegisterPage,
              setPage: setAutoRegisterPage,
              order: "asc",
              orderBy: "",
              rowsPerPage: autoRegisterRowsPerPage,
              setRowsPerPage: setAutoRegisterRowsPerPage,
              setOrder: () => {},
              setOrderBy: () => {},
              allowPagination: true,
            }}
          />
        </Box>
      )}

      {suggestedCoursesSkus.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <T id="suggestedCoursesTitle" />
          </Typography>
          <LazyTable
            {...{
              rowCount: suggestedCoursesData?.totalRecords ?? 0,
              rows: suggestedCoursesSkus,
              loading: isFetchingSuggestedCourses,
              dense: true,
              cells: skuRelationshipCells,
              page: suggestedCoursesPage,
              setPage: setSuggestedCoursesPage,
              order: "asc",
              orderBy: "",
              rowsPerPage: suggestedCoursesRowsPerPage,
              setRowsPerPage: setSuggestedCoursesRowsPerPage,
              setOrder: () => {},
              setOrderBy: () => {},
              allowPagination: true,
            }}
          />
        </Box>
      )}

      {includedInSkus.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <T id="includedInTitle" />
          </Typography>
          <LazyTable
            {...{
              rowCount: includedInData?.totalRecords ?? 0,
              rows: includedInSkus as any[],
              loading: isFetchingIncludedIn,
              dense: true,
              cells: includedInCells,
              page: includedInPage,
              setPage: setIncludedInPage,
              order: includedInOrder,
              setOrder: setIncludedInOrder,
              orderBy: includedInOrderBy,
              setOrderBy: setIncludedInOrderBy,
              rowsPerPage: includedInRowsPerPage,
              setRowsPerPage: setIncludedInRowsPerPage,
              allowPagination: true,
            }}
          />
        </Box>
      )}

      {sku?.hasChild && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            <T id="containsTitle" />
          </Typography>
          <SkuContents sku={sku} readonly={true} />
        </Box>
      )}
    </Box>
  );
};

export default withErrorBoundary(SkuRelationships);
